import React, { useRef, useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';

interface Point {
  x: number;
  y: number;
}

interface Prediction {
  points: Point[];
  class: string;
  confidence?: number;
}

interface SegmentationResult {
  predictions: Prediction[];
}

interface SegmentedImageProps {
  imageBase64: string;
  segmentationResult?: SegmentationResult;
  lineWidth?: number;
  lineColor?: string;
  alt?: string;
  style?: React.CSSProperties;
  usePreRendered?: boolean;
  preRenderedBase64?: string;
}

/**
 * Component that displays an image with segmentation overlay drawn dynamically
 * This reduces backend load by drawing the segmentation lines in the browser
 */
const SegmentedImage: React.FC<SegmentedImageProps> = ({
  imageBase64,
  segmentationResult,
  lineWidth = 5,
  lineColor = '#ff0000',
  alt = 'Segmented image',
  style,
  usePreRendered = false,
  preRenderedBase64,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // If using pre-rendered image, skip canvas drawing
    if (usePreRendered && preRenderedBase64) {
      setIsLoading(false);
      return;
    }

    // If no segmentation result but we have pre-rendered image, use that as fallback
    if (!segmentationResult && preRenderedBase64) {
      console.log('SegmentedImage: Using pre-rendered fallback');
      setIsLoading(false);
      return;
    }

    if (!imageBase64) {
      console.warn('SegmentedImage: No imageBase64 provided');
      setIsLoading(false);
      return;
    }

    if (!segmentationResult) {
      console.warn('SegmentedImage: No segmentationResult provided, showing original image');
      setIsLoading(false);
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = new Image();
    img.onload = () => {
      // Set canvas dimensions to match image
      canvas.width = img.width;
      canvas.height = img.height;

      // Draw the original image
      ctx.drawImage(img, 0, 0);

      // Draw segmentation polygons
      const predictions = segmentationResult.predictions || [];
      console.log('SegmentedImage: Drawing', predictions.length, 'predictions');

      predictions.forEach((prediction, idx) => {
        const points = prediction.points || [];
        console.log(`Prediction ${idx}:`, points.length, 'points');

        if (points.length === 0) return;

        ctx.strokeStyle = lineColor;
        ctx.lineWidth = lineWidth;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        // Draw the polygon
        ctx.beginPath();
        ctx.moveTo(points[0].x, points[0].y);

        for (let i = 1; i < points.length; i++) {
          ctx.lineTo(points[i].x, points[i].y);
        }

        // Close the polygon
        ctx.closePath();
        ctx.stroke();
      });

      setIsLoading(false);
    };

    img.onerror = () => {
      console.error('SegmentedImage: Failed to load image');
      setIsLoading(false);
    };

    // Load the base64 image
    img.src = `data:image/jpeg;base64,${imageBase64}`;
  }, [imageBase64, segmentationResult, lineWidth, lineColor, usePreRendered, preRenderedBase64]);

  // Use pre-rendered image if available and either explicitly requested or no segmentation data
  if ((usePreRendered || !segmentationResult) && preRenderedBase64) {
    return (
      <Box sx={{ position: 'relative', ...style }}>
        <img
          src={`data:image/png;base64,${preRenderedBase64}`}
          alt={alt}
          style={{ width: '100%', height: 'auto', display: 'block' }}
        />
      </Box>
    );
  }

  // If no segmentation result and no pre-rendered, show original image
  if (!segmentationResult && imageBase64) {
    return (
      <Box sx={{ position: 'relative', ...style }}>
        <img
          src={`data:image/jpeg;base64,${imageBase64}`}
          alt={alt}
          style={{ width: '100%', height: 'auto', display: 'block' }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', ...style }}>
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1,
          }}
        >
          <CircularProgress />
        </Box>
      )}
      <canvas
        ref={canvasRef}
        style={{
          width: '100%',
          height: 'auto',
          display: isLoading ? 'none' : 'block',
        }}
      />
    </Box>
  );
};

export default SegmentedImage;

